import { PlusCircle } from 'lucide-react-native';
import { useState } from 'react';
import { HStack, Text, VStack } from '../../../components/primitives';
import { Button } from '../../../components/primitives/Button';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { AutoCallSettings } from '../../voice/components/AutoCallSettings';
import { VoiceTemplateForm } from '../../voice/components/VoiceTemplateForm';
import { useCreateVoiceTemplate, useVoiceTemplates } from '../../voice/hooks/useVoice';
import type { CreateVoiceTemplateInput } from '../../voice/types';
import { SettingsSection } from './SettingsSection';

export function VoiceCallingSection() {
  const { t } = useTranslation();
  const { currentOrganization, backendUser } = useAuth();
  const orgId = currentOrganization?.id ?? '';
  const canManage = backendUser?.role === 'admin' || backendUser?.role === 'manager';

  const { data: templates, isLoading } = useVoiceTemplates(canManage ? orgId : '');
  const createTemplate = useCreateVoiceTemplate();
  const [showCreateForm, setShowCreateForm] = useState(false);

  if (!canManage) {
    return (
      <SettingsSection title={t('settings.voiceCalling.title')}>
        <Text variant="body" color={colors.text.secondary} testID="voice-calling-restricted">
          {t('settings.voiceCalling.restricted')}
        </Text>
      </SettingsSection>
    );
  }

  const hasTemplates = (templates?.length ?? 0) > 0;
  const showForm = showCreateForm || (!isLoading && !hasTemplates);

  const handleSubmit = async (data: CreateVoiceTemplateInput) => {
    await createTemplate.mutateAsync(data);
    setShowCreateForm(false);
  };

  return (
    <SettingsSection
      title={t('settings.voiceCalling.title')}
      description={t('settings.voiceCalling.description')}
    >
      {showForm ? (
        <VoiceTemplateForm
          organizationId={orgId}
          onSubmit={(data) => handleSubmit(data as CreateVoiceTemplateInput)}
          onCancel={() => setShowCreateForm(false)}
          isSubmitting={createTemplate.isPending}
        />
      ) : (
        <VStack spacing="md">
          <HStack justify="end">
            <Button
              variant="secondary"
              size="sm"
              onPress={() => setShowCreateForm(true)}
              testID="voice-calling-new-template"
            >
              <HStack spacing="xs" align="center">
                <PlusCircle size={14} color={colors.text.primary} />
                <Text variant="caption" color={colors.text.primary}>
                  {t('settings.voiceCalling.newTemplate')}
                </Text>
              </HStack>
            </Button>
          </HStack>
          <AutoCallSettings organizationId={orgId} />
        </VStack>
      )}
    </SettingsSection>
  );
}
